import React, { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { Box, Link } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { LinkButton } from '@alycecom/ui';

import { getStatuses } from '../../modules/RecipientModule/store/statuses/statuses.selectors';
import useTrackRecipient from '../../hooks/useTrackRecipient';
import { getBranding } from '../../store/branding/branding.selectors';
import { BrandingFields } from '../../types/Branding.types';

const useStyles = makeStyles(({ spacing, breakpoints }) => ({
  root: {
    position: 'relative',
    textAlign: 'center',
    fontFamily: 'Lato',
    fontSize: 12,
    lineHeight: 1.33,
    display: 'flex',
    justifyContent: 'center',
    paddingBottom: spacing(2),
  },
  company: {
    marginRight: spacing(1.5),
  },
  link: {
    marginRight: spacing(1.5),
    display: 'inline-block',
    color: 'inherit',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'none',
    },
    cursor: 'pointer',
  },
  supportLink: {
    fontWeight: 'normal',
  },
  mobileHidden: {
    [breakpoints.down('sm')]: {
      display: 'none',
    },
  },
}));

export interface IFooterProps {
  textColor?: string;
  showSupport?: boolean;
  mobileHidden?: boolean;
}

const Footer = ({ textColor = 'grey.regular', showSupport = true, mobileHidden = false }: IFooterProps) => {
  const trackEvent = useTrackRecipient();
  const classes = useStyles();
  const statuses = useSelector(getStatuses);
  const customStyling = useSelector(getBranding);

  const canShowSupportForm = !statuses.draft && !statuses.preview;

  const handleSupport = useCallback(() => {
    if (canShowSupportForm) {
      trackEvent('Footer - Clicked contact support link');
      window.open(customStyling[BrandingFields.contactSupportLink], '_blank');
    }
  }, [canShowSupportForm, trackEvent, customStyling]);

  const handleClickPrivacyPolicy = useCallback(() => trackEvent('Footer - Clicked privacy police link'), [trackEvent]);

  const handleClickTermsOfUse = useCallback(() => trackEvent('Footer - Clicked terms of use link'), [trackEvent]);

  return (
    <Box className={classNames(classes.root, { [classes.mobileHidden]: mobileHidden })} color={textColor}>
      <span className={classes.company}>Powered by {customStyling[BrandingFields.companyName]}</span>
      {customStyling.termsUrl && (
        <Link
          href={customStyling.termsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.link}
          onClick={handleClickTermsOfUse}
          underline="hover"
        >
          Terms Of Use
        </Link>
      )}
      <Link
        href={customStyling[BrandingFields.privacyPolicyLink] || 'https://www.alyce.com/privacy-policy'}
        target="_blank"
        rel="noopener noreferrer"
        className={classes.link}
        onClick={handleClickPrivacyPolicy}
        underline="hover"
      >
        Privacy policy
      </Link>
      {showSupport && (
        <LinkButton onClick={handleSupport} className={classNames(classes.link, classes.supportLink)}>
          Contact Support
        </LinkButton>
      )}
    </Box>
  );
};

export default memo(Footer);
